import { useState, useEffect, useContext, useRef } from "react";
import { UserContext } from "../../../../../../context/UserContext";
import { options } from "../util";
import {
  Stack,
  Typography,
  TextField as MuiTextField,
  Autocomplete as MuiAutocomplete,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Field } from "formik";
import { FormContainer, NavButtons } from "../../../../Menu/Flow";
import { FlowHandlersContext } from "../../../../Menu/FlowContainer";
import api from "../../../../../../api/axios";
import employerFlowStyles from "../../styles";
import { styles } from "../../../../../../legacyComponents/componentsV2/FlowV2/Fields/util";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export default function ReimbursementAccount(props) {
  const { mode, exitUrl, step, formik, displayOnly } = props;
  const {
    errors,
    touched,
    values,
    setFieldValue,
    setValues,
    setFieldTouched,
    setErrors,
  } = formik;
  const handlers = useContext(FlowHandlersContext);
  const { user, getEmployer } = useContext(UserContext);

  const employer = getEmployer();

  // Bank search state
  const [banksState, setBanksState] = useState([]);
  const [allBanks, setAllBanks] = useState([]);
  const [bankInputValue, setBankInputValue] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Verification status of the stored bank account (shown when set)
  const [verifiedTs, setVerifiedTs] = useState(null);
  const [banksLoaded, setBanksLoaded] = useState(false);

  const initialBankMatchDoneRef = useRef(false);

  // ── Load banks ────────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchBanks() {
      try {
        const response = await api.get("/bank");
        const banks = response?.data?.data || [];
        setAllBanks(banks);
      } catch (error) {
        setAllBanks([]);
      } finally {
        setBanksLoaded(true);
      }
    }
    fetchBanks();
  }, []);

  // ── Initialize once employer is available ─────────────────────────────────
  useEffect(() => {
    if (!employer) return;
    if (initialized) return;
    console.log("employer = ", employer);
    const existingAccount =
      employer?.employerAccount?.employeeReimbursementAccount || null;
    const isBilling =
      employer?.employerAccount?.billingAccountId ===
      employer?.employerAccount?.employeeReimbursementAccountId;

    setVerifiedTs(existingAccount?.verifiedTs || null);

    setFieldValue("isBillingAccount", isBilling ? "Yes" : "No", false);
    setFieldValue(
      "accountType",
      existingAccount?.accountType || "checking",
      false,
    );
    setFieldValue("nickname", existingAccount?.nickname || "", false);
    setFieldValue("accountNumber", existingAccount?.accountNumber || "", false);
    setFieldValue("bankName", existingAccount?.bankName || "", false);

    const routingNumber = existingAccount?.routingNumber || "";
    setBankInputValue(routingNumber);

    setInitialized(true);
  }, [employer]);

  // ── One-time match of saved routing number to a bank object (existing accounts only)
  useEffect(() => {
    if (initialBankMatchDoneRef.current) return;
    if (!initialized || !banksLoaded) return;
    initialBankMatchDoneRef.current = true;

    if (!bankInputValue) return;
    if (values.bank?.plaidVerificationIssueTs !== undefined) return;

    const matchedBank = allBanks.find(
      (b) => b.routingNumber === bankInputValue,
    );
    if (matchedBank) {
      setFieldValue(
        "bank",
        {
          ...matchedBank,
          displayName: `${matchedBank.routingNumber} - ${matchedBank.bankName}`,
        },
        false,
      );
      setFieldValue("bankName", matchedBank.bankName, false);
    }
  }, [initialized, banksLoaded]);

  // ── Bank search ───────────────────────────────────────────────────────────
  function searchBanks(routingNumber) {
    if (!routingNumber) {
      setBanksState([]);
      return;
    }
    const foundBanks = allBanks.filter((bank) => {
      const routingMatch = bank.routingNumber?.includes(routingNumber);
      const nameMatch = bank.bankName
        ?.toLowerCase()
        .includes(routingNumber.toLowerCase());
      return routingMatch || nameMatch;
    });
    if (!foundBanks || foundBanks.length === 0) {
      setBanksState([]);
      return;
    }
    const formattedBanks = foundBanks.slice(0, 5).map((bank) => ({
      ...bank,
      displayName: `${bank?.routingNumber || ""} - ${bank?.bankName || ""}`,
    }));
    setBanksState(formattedBanks);
  }

  function handleBankInput(routingNumber) {
    setBankInputValue(routingNumber || "");
    if (routingNumber) {
      searchBanks(routingNumber);
    } else {
      setBanksState([]);
    }
    // Clear selection — user must re-pick from the dropdown
    setFieldValue("bank", null, false);
    setFieldValue("bankName", "", false);
    // Any routing number change invalidates verification
    setVerifiedTs(null);
  }

  function handleBankSelect(event, bank) {
    setFieldValue("isBillingAccount", "Yes", false);
    if (bank?.bankName) {
      bank = {
        ...bank,
        id: bank?.id ?? formik?.values?.bank?.id,
        accountType: values.accountType || "",
        displayName: `${bank?.routingNumber || ""} - ${bank?.bankName || ""}`,
        routingNumber: bank?.routingNumber || "",
      };
      handleFields(bank, true);
      setBankInputValue(bank?.routingNumber || "");
    } else {
      const routingNumber =
        typeof bank === "string" ? bank : bank?.routingNumber || "";
      setValues({
        ...values,
        bank: { ...options.nullOption(), routingNumber: routingNumber },
        bankName: "",
        nickname: values.nickname || "",
        accountType: values.accountType || "",
        accountNumber: values.accountNumber || "",
        routingNumber: routingNumber || "",
      });
      setBankInputValue(routingNumber || "");
    }
    const updatedErrors = { ...errors };
    delete updatedErrors.bank;
    delete updatedErrors.nickname;
    delete updatedErrors.accountType;
    delete updatedErrors.accountNumber;
    delete updatedErrors.routingNumber;
    setErrors(updatedErrors);

    setFieldTouched("bank", false, false);
    setFieldTouched("bankName", false, false);
    setFieldTouched("nickname", false, false);
    setFieldTouched("accountType", false, false);
    setFieldTouched("accountNumber", false, false);
    setFieldTouched("routingNumber", false, false);

    // Any bank change invalidates verification
    setVerifiedTs(null);
  }

  function handleFields(bank, validate = false) {
    const routingNumberValue = bank?.routingNumber || "";
    const reimbursementForm = {
      ...values,
      bank,
      bankName: bank?.bankName || values.bankName || "",
      nickname: bank?.bankName || values.nickname || "",
      accountType: values.accountType || bank?.accountType || "",
      accountNumber: values.accountNumber || "",
      routingNumber: routingNumberValue || "",
    };
    setValues(reimbursementForm, validate);
  }

  const handleAccountTypeChange = (e) => {
    setFieldValue("accountType", e.target.value);
  };

  const handleIsBillingAccountChange = (e) => {
    setFieldValue("isBillingAccount", e.target.value);
    setFieldTouched("isBillingAccount", true, false);
  };

  // ── Misc ──────────────────────────────────────────────────────────────────
  const hideNext = mode === "manage";

  const showCompleteSetupLater =
    !hideNext &&
    employer?.partner?.id &&
    employer?.partner?.id === user?.partnerId;

  if (!initialized || !banksLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        {/* Complete Setup Later banner */}
        {/* Complete Setup Later banner */}
        {showCompleteSetupLater && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#f8f9fc",
              border: "1px solid #ACBEE8",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
              Complete Setup Later
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Reimbursement setup can be completed now or later by you or the
              employer. You can continue setup by navigating to the
              Administrator Dashboard to add employees and their spouses and
              dependents.
            </Typography>
            <Typography
              component="a"
              href={`/employer/${employer.id}`}
              sx={{
                display: "block",
                marginTop: "8px",
                color: "#6C63FF",
                fontSize: "14px",
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": { color: "#5650d8" },
              }}
            >
              Go to Administrator Dashboard
            </Typography>
          </Box>
        )}

        <Typography variant="h2" sx={employerFlowStyles.h2}>
          Reimbursement Account
        </Typography>
        <Typography>
          Add or update the company bank account Salusion will use to fund
          employee reimbursements.
        </Typography>

        <Stack spacing={2} sx={{ marginTop: "40px" }}>
          {/* Routing Number + Bank Name */}
          <Stack direction="row" spacing={2}>
            <Stack flex={1} sx={{ position: "relative" }}>
              <Typography sx={{ marginBottom: "8px" }}>
                Routing Number
              </Typography>
              <Field name="bank">
                {({ field }) => (
                  <MuiAutocomplete
                    value={values.bank || null}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleBankSelect(event, newValue);
                        field.onChange({
                          target: { name: field.name, value: newValue },
                        });
                      } else {
                        handleBankInput("");
                        field.onChange({
                          target: { name: field.name, value: null },
                        });
                      }
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "reset") return;
                      handleBankInput(newInputValue);
                    }}
                    inputValue={bankInputValue ?? ""}
                    getOptionLabel={(option) =>
                      option?.displayName || option?.routingNumber || ""
                    }
                    options={banksState}
                    freeSolo
                    disabled={displayOnly}
                    renderInput={(params) => (
                      <MuiTextField
                        {...params}
                        error={!!(errors.bank && touched.bank)}
                        helperText={
                          errors.bank && touched.bank ? errors.bank : ""
                        }
                        placeholder="Enter routing number and select bank from dropdown list"
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "14px",
                            backgroundColor: displayOnly ? "#f3f4f6" : "white",
                            "& fieldset": {
                              borderColor:
                                errors.bank && touched.bank
                                  ? "#d32f2f"
                                  : "#ACBEE8",
                              borderWidth: "1px",
                            },
                            "&:hover fieldset": {
                              borderColor:
                                errors.bank && touched.bank
                                  ? "#d32f2f"
                                  : "#ACBEE8",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor:
                                errors.bank && touched.bank
                                  ? "#d32f2f"
                                  : "#ACBEE8",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#f3f4f6",
                              "& fieldset": { borderColor: "#ACBEE8" },
                            },
                          },
                          "& .MuiOutlinedInput-input": { padding: "14px" },
                        }}
                      />
                    )}
                    sx={{ width: "100%" }}
                    slotProps={{
                      popper: {
                        sx: {
                          marginLeft: "0 !important",
                          "& .MuiPaper-root": {
                            marginLeft: "0 !important",
                            fontSize: "14px",
                            "& .MuiAutocomplete-listbox": {
                              fontSize: "14px",
                              "& .MuiAutocomplete-option": { fontSize: "14px" },
                            },
                          },
                        },
                      },
                    }}
                  />
                )}
              </Field>
            </Stack>

            <Stack flex={1}>
              <Typography sx={{ marginBottom: "8px" }}>Bank Name</Typography>
              <Field name="bankName">
                {({ field }) => (
                  <MuiTextField
                    {...field}
                    value={field.value || ""}
                    fullWidth
                    disabled={true}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontSize: "14px",
                        backgroundColor: "#f3f4f6",
                        cursor: "not-allowed",
                        "& fieldset": {
                          borderColor: "#ACBEE8",
                          borderWidth: "1px",
                        },
                      },
                      "& .MuiOutlinedInput-input": { padding: "14px" },
                    }}
                  />
                )}
              </Field>
            </Stack>
          </Stack>

          {/* Nickname + Account Type */}
          <Stack direction="row" spacing={2}>
            <Stack flex={1}>
              <Typography sx={{ marginTop: "20px", marginBottom: "8px" }}>
                Nickname
              </Typography>
              <Field name="nickname">
                {({ field }) => (
                  <MuiTextField
                    {...field}
                    value={field.value || ""}
                    fullWidth
                    disabled={displayOnly}
                    error={!!(errors.nickname && touched.nickname)}
                    helperText={
                      errors.nickname && touched.nickname ? errors.nickname : ""
                    }
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontSize: "14px",
                        backgroundColor: displayOnly ? "#f3f4f6" : "white",
                        "& fieldset": {
                          borderColor:
                            errors.nickname && touched.nickname
                              ? "#d32f2f"
                              : "#ACBEE8",
                          borderWidth: "1px",
                        },
                        "&:hover fieldset": {
                          borderColor:
                            errors.nickname && touched.nickname
                              ? "#d32f2f"
                              : "#ACBEE8",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor:
                            errors.nickname && touched.nickname
                              ? "#d32f2f"
                              : "#ACBEE8",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#f3f4f6",
                          "& fieldset": { borderColor: "#ACBEE8" },
                        },
                      },
                      "& .MuiOutlinedInput-input": { padding: "14px" },
                    }}
                  />
                )}
              </Field>
            </Stack>

            <Stack flex={1}>
              <Typography sx={{ marginTop: "20px", marginBottom: "8px" }}>
                Account Type
              </Typography>
              <Field name="accountType">
                {({ field }) => (
                  <select
                    value={values.accountType || ""}
                    onChange={(e) => {
                      handleAccountTypeChange(e);
                      field.onChange(e);
                    }}
                    disabled={displayOnly}
                    style={{
                      ...styles.select(!field.value),
                      border: `1px solid ${errors.accountType && touched.accountType ? "#d32f2f" : "#ACBEE8"}`,
                    }}
                  >
                    <option value="">Select account type</option>
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                  </select>
                )}
              </Field>
              {errors.accountType && touched.accountType && (
                <div className="error-message-fields">{errors.accountType}</div>
              )}
            </Stack>
          </Stack>

          {/* Account Number + Billing */}
          <Stack direction="row" spacing={2}>
            <Stack flex={1}>
              <Typography sx={{ marginTop: "20px", marginBottom: "8px" }}>
                Account Number
              </Typography>
              <Field name="accountNumber">
                {({ field }) => (
                  <MuiTextField
                    {...field}
                    value={field.value || ""}
                    fullWidth
                    disabled={displayOnly}
                    inputMode="numeric"
                    onKeyDown={(e) => {
                      // Allow control keys (backspace, delete, arrows, tab, etc.) and copy/paste shortcuts
                      if (e.key.length > 1 || e.ctrlKey || e.metaKey) {
                        return;
                      }
                      // Block anything that isn't a digit
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      // Safety net for pasted content
                      const cleaned = e.target.value.replace(/\D/g, "");
                      setFieldValue("accountNumber", cleaned);
                      // Any account number change invalidates verification
                      setVerifiedTs(null);
                    }}
                    error={!!(errors.accountNumber && touched.accountNumber)}
                    helperText={
                      errors.accountNumber && touched.accountNumber
                        ? errors.accountNumber
                        : ""
                    }
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontSize: "14px",
                        backgroundColor: displayOnly ? "#f3f4f6" : "white",
                        "& fieldset": {
                          borderColor:
                            errors.accountNumber && touched.accountNumber
                              ? "#d32f2f"
                              : "#ACBEE8",
                          borderWidth: "1px",
                        },
                        "&:hover fieldset": {
                          borderColor:
                            errors.accountNumber && touched.accountNumber
                              ? "#d32f2f"
                              : "#ACBEE8",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor:
                            errors.accountNumber && touched.accountNumber
                              ? "#d32f2f"
                              : "#ACBEE8",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#f3f4f6",
                          "& fieldset": { borderColor: "#ACBEE8" },
                        },
                      },
                      "& .MuiOutlinedInput-input": { padding: "14px" },
                    }}
                  />
                )}
              </Field>
            </Stack>
            <Stack flex={1}>
              <Typography sx={{ marginTop: "20px", marginBottom: "8px" }}>
                Charge Salusion fees to this account?
              </Typography>
              <Field name="isBillingAccount">
                {({ field }) => (
                  <select
                    value={values.isBillingAccount || ""}
                    onChange={(e) => {
                      handleIsBillingAccountChange(e);
                      field.onChange(e);
                    }}
                    disabled={displayOnly}
                    style={{
                      ...styles.select(!field.value),
                      border: `1px solid ${errors.isBillingAccount && touched.isBillingAccount ? "#d32f2f" : "#ACBEE8"}`,
                    }}
                  >
                    <option value="">Select yes or no</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                )}
              </Field>
              {errors.isBillingAccount && touched.isBillingAccount && (
                <div className="error-message-fields">
                  {errors.isBillingAccount}
                </div>
              )}
            </Stack>
          </Stack>

          {/* Verified confirmation message */}
          {verifiedTs && (
            <Alert
              icon={<CheckCircleIcon fontSize="inherit" />}
              severity="success"
              sx={{ mt: 2 }}
            >
              This bank account has been verified. Please continue.
            </Alert>
          )}
        </Stack>
      </FormContainer>

      {/* ACH authorization reminder */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          backgroundColor: "#f8f9fc",
          border: "1px solid #ACBEE8",
          borderRadius: "8px",
        }}
      >
        <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
          Make sure your bank will allow ACH withdrawals from Salusion
        </Typography>
        <Typography sx={{ fontSize: "14px", marginBottom: "8px" }}>
          If your bank uses Positive Pay, ACH filters, or account verification,
          please approve Salusion's ACH Company ID to avoid rejected ACH
          transactions and related fees.
        </Typography>
        <Typography sx={{ fontSize: "14px" }}>
          Reimbursement funding: ACH Company ID <strong>2844032944</strong>
        </Typography>
        <Typography sx={{ fontSize: "14px" }}>
          Billing: ACH Company ID <strong>1844032944</strong>
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack spacing={2} sx={{ flex: 1 }}>
          <NavButtons
            mode={mode}
            enableSave={true}
            exitUrl={exitUrl}
            step={step}
            hideNext={hideNext}
          />
        </Stack>
      </Box>
    </Stack>
  );
}
